import {
    ProjectSelectableChapter,
    ProjectSelectableResource,
    ProjectSelectableTopic,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { Resource_ResourceEnum } from '@protos/content_management/content.db_pb';
import { ISelectedProjectResource } from './reducer/projectFlow.Slice';

/**
 * Selection helpers for the project resource picker.
 *
 * Two rules from the API drive all of this:
 * - a project spans exactly one chapter, so picking in a new chapter replaces
 *   whatever was picked before;
 * - selection is deduped by resourceId, because the same resource can appear in
 *   two topics of a chapter and creation stores it once.
 */

export const isResourceSelected = (
    selectedResources: ISelectedProjectResource[],
    resourceId: string
) => selectedResources.some((r) => r.resourceId === resourceId);

const toSelection = (
    topic: ProjectSelectableTopic,
    resource: ProjectSelectableResource
): ISelectedProjectResource => ({
    resourceId: resource.resourceId,
    lessonId: topic.lessonId,
    moduleId: topic.topicId,
});

/** Practicals are mandatory - a selected chapter always carries all of them. */
export const isPracticalResource = (resource: ProjectSelectableResource) =>
    resource.resourceType === Resource_ResourceEnum.RESOURCE_TYPE_PRACTICAL;

/** Every distinct resource of a chapter, in the order the API returned them. */
export const getChapterResourceSelections = (
    chapter?: ProjectSelectableChapter,
    include: (resource: ProjectSelectableResource) => boolean = () => true
): ISelectedProjectResource[] => {
    const selections: ISelectedProjectResource[] = [];
    (chapter?.topics ?? []).forEach((topic) => {
        (topic.resources ?? []).forEach((resource) => {
            if (
                include(resource) &&
                !isResourceSelected(selections, resource.resourceId)
            ) {
                selections.push(toSelection(topic, resource));
            }
        });
    });
    return selections;
};

/**
 * Order picks the way the API returned them - topics by topic_no, then each
 * topic's resource order. That order, not the order the teacher clicked in,
 * is the sequence the project is created with. Picks the chapter no longer
 * has go last rather than being dropped, so creation can still reject them.
 */
export const sortByChapterOrder = (
    chapter: ProjectSelectableChapter | undefined,
    resources: ISelectedProjectResource[]
): ISelectedProjectResource[] => {
    const rank = new Map(
        getChapterResourceSelections(chapter).map((r, index) => [
            r.resourceId,
            index,
        ])
    );
    const rankOf = (r: ISelectedProjectResource) =>
        rank.get(r.resourceId) ?? Number.MAX_SAFE_INTEGER;
    return [...resources].sort((a, b) => rankOf(a) - rankOf(b));
};

/** Distinct resource count of a chapter, falling back to the counted topics. */
export const getChapterResourceCount = (chapter?: ProjectSelectableChapter) =>
    chapter?.totalResourceCount || getChapterResourceSelections(chapter).length;

export interface IProjectSelection {
    chapterId?: number;
    resources: ISelectedProjectResource[];
}

/** How many of the current picks belong to `chapter`. */
export const getSelectedCountForChapter = (
    selection: IProjectSelection,
    chapter: ProjectSelectableChapter
) =>
    selection.chapterId === chapter.chapterId
        ? selection.resources.length
        : 0;

/** How many of `topic`'s resources are currently picked. */
export const getSelectedCountForTopic = (
    selection: IProjectSelection,
    chapter: ProjectSelectableChapter,
    topic: ProjectSelectableTopic
) =>
    selection.chapterId === chapter.chapterId
        ? (topic.resources ?? []).filter((r) =>
              isResourceSelected(selection.resources, r.resourceId)
          ).length
        : 0;

/** Add or remove a single resource, switching chapter if the picker moved. */
export const toggleResourceSelection = (
    selection: IProjectSelection,
    chapter: ProjectSelectableChapter,
    topic: ProjectSelectableTopic,
    resource: ProjectSelectableResource
): IProjectSelection => {
    if (selection.chapterId !== chapter.chapterId) {
        // Moving to a chapter starts from its practicals plus the picked
        // resource, in the order the API returned them.
        return {
            chapterId: chapter.chapterId,
            resources: getChapterResourceSelections(
                chapter,
                (r) => isPracticalResource(r) || r.resourceId === resource.resourceId
            ),
        };
    }
    if (isPracticalResource(resource)) return selection;

    const resources = selection.resources;
    const next = isResourceSelected(resources, resource.resourceId)
        ? resources.filter((r) => r.resourceId !== resource.resourceId)
        : sortByChapterOrder(chapter, [
              ...resources,
              toSelection(topic, resource),
          ]);

    return {
        chapterId: next.length ? chapter.chapterId : undefined,
        resources: next,
    };
};

/** Select every resource of a topic, or remove them when it is already whole. */
export const toggleTopicSelection = (
    selection: IProjectSelection,
    chapter: ProjectSelectableChapter,
    topic: ProjectSelectableTopic
): IProjectSelection => {
    const topicResourceIds = new Set(
        (topic.resources ?? []).map((r) => r.resourceId)
    );
    if (selection.chapterId !== chapter.chapterId) {
        return {
            chapterId: chapter.chapterId,
            resources: getChapterResourceSelections(
                chapter,
                (r) => isPracticalResource(r) || topicResourceIds.has(r.resourceId)
            ),
        };
    }

    const isFullySelected = [...topicResourceIds].every((id) =>
        isResourceSelected(selection.resources, id)
    );
    if (!isFullySelected) {
        return {
            chapterId: chapter.chapterId,
            resources: sortByChapterOrder(chapter, [
                ...selection.resources,
                ...getChapterResourceSelections(
                    chapter,
                    (r) =>
                        topicResourceIds.has(r.resourceId) &&
                        !isResourceSelected(selection.resources, r.resourceId)
                ),
            ]),
        };
    }

    // Practicals stay while anything else is picked; otherwise clear the chapter.
    const practicalIds = new Set(
        getChapterResourceSelections(chapter, isPracticalResource).map(
            (r) => r.resourceId
        )
    );
    const remaining = selection.resources.filter(
        (r) => !topicResourceIds.has(r.resourceId) || practicalIds.has(r.resourceId)
    );
    return remaining.some((r) => !practicalIds.has(r.resourceId))
        ? { chapterId: chapter.chapterId, resources: remaining }
        : { chapterId: undefined, resources: [] };
};

/**
 * Resolve the picked resources back to their display data, in selection order
 * (see sortByChapterOrder) - the order that maps 1-based onto
 * `selectedResourcesInfo[].sequence` at creation.
 */
export const resolveSelectedResources = (
    chapter: ProjectSelectableChapter | undefined,
    selectedResources: ISelectedProjectResource[]
) =>
    selectedResources
        .map((selected) => {
            const topic = (chapter?.topics ?? []).find(
                (t) => t.lessonId === selected.lessonId
            );
            const resource = (topic?.resources ?? []).find(
                (r) => r.resourceId === selected.resourceId
            );
            return resource ? { topic, resource } : undefined;
        })
        .filter(
            (
                item
            ): item is {
                topic: ProjectSelectableTopic | undefined;
                resource: ProjectSelectableResource;
            } => item !== undefined
        );

/**
 * The picked practical, i.e. the one resource a teacher solution can be saved
 * against - a project carries exactly one. `undefined` when the selection has
 * none, in which case creation must send no `metaData` at all: the API rejects
 * `practical` on any other resource type.
 */
export const getSelectedPracticalResourceId = (
    chapter: ProjectSelectableChapter | undefined,
    selectedResources: ISelectedProjectResource[]
) => {
    const resourceTypes = new Map<string, Resource_ResourceEnum>();
    (chapter?.topics ?? []).forEach((topic) => {
        (topic.resources ?? []).forEach((resource) => {
            resourceTypes.set(resource.resourceId, resource.resourceType);
        });
    });

    return selectedResources.find(
        (selected) =>
            resourceTypes.get(selected.resourceId) ===
            Resource_ResourceEnum.RESOURCE_TYPE_PRACTICAL
    )?.resourceId;
};
